'use client';

import { Button } from '@crabs-ai/ds-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCodeEditableFields,
  updateCodeEditableField,
} from '../code-data-editing';
import {
  isAuthoringWritingGuideCodeItem,
  type CodeItem,
} from '../items/code/code-item-type';
import { changeItems } from '../state/actions/change-items';
import { useDimensions, useWriteContext } from '../utils/use-context';
import { WritingGuideInspector } from './writing-guide-inspector';

interface CodeInspectorProps {
  item: CodeItem;
}

/**
 * CodeItem 의 TSX 코드를 직접 편집할 수 있는 inspector.
 *
 * - 로컬 state 로 편집 → "반영하기" 버튼 클릭 시 EditorState 의 item.code 갱신
 * - item.code 가 외부에서 바뀌면 (재생성/undo 등) 로컬 state 동기화 — 단, dirty 상태일 땐 사용자 편집 보존
 * - compiledError 가 있으면 표시 (반영 후 컴파일 실패 시)
 */
export function CodeInspector({ item }: CodeInspectorProps) {
  return isAuthoringWritingGuideCodeItem(item) ? (
    <WritingGuideInspector item={item} />
  ) : (
    <GeneratedCodeInspector item={item} />
  );
}

function GeneratedCodeInspector({ item }: CodeInspectorProps) {
  const { setState } = useWriteContext();
  const { compositionWidth, compositionHeight } = useDimensions();
  const [localCode, setLocalCode] = useState(item.code);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(
    item.codeActiveField ?? item.shinhanPdfActiveField ?? null,
  );
  const [fieldValue, setFieldValue] = useState('');
  const prevItemIdRef = useRef(item.id);
  const fieldTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const focusFieldEditor = () => {
    window.requestAnimationFrame(() => {
      fieldTextAreaRef.current?.focus();
      fieldTextAreaRef.current?.select();
    });
  };

  const editableFields = useMemo(
    () =>
      getCodeEditableFields({
        code: localCode,
        compositionWidth,
        compositionHeight,
      }),
    [compositionHeight, compositionWidth, localCode],
  );

  const selectedField = useMemo(
    () =>
      editableFields.find((field) => field.key === selectedFieldKey) ?? null,
    [selectedFieldKey, editableFields],
  );

  // 다른 item 으로 전환되거나 외부에서 item.code 가 바뀔 때 로컬 state 동기화.
  // - item.id 가 바뀌면 무조건 재초기화 (다른 item 으로 넘어왔으므로 이전 편집본 버림)
  // - 같은 item 의 외부 변경은 dirty 가 아닐 때만 동기화 (사용자 편집 보존)
  useEffect(() => {
    if (prevItemIdRef.current !== item.id) {
      prevItemIdRef.current = item.id;
      setLocalCode(item.code);
      setIsDirty(false);
      setSelectedFieldKey(
        item.codeActiveField ?? item.shinhanPdfActiveField ?? null,
      );
      return;
    }
    if (!isDirty) {
      setLocalCode(item.code);
    }
    // isDirty 자체 변화로는 동기화 안 함
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.code]);

  useEffect(() => {
    const activeKey = item.codeActiveField ?? item.shinhanPdfActiveField;

    if (activeKey && editableFields.some((field) => field.key === activeKey)) {
      setSelectedFieldKey(activeKey);
      return;
    }

    setSelectedFieldKey((currentKey) => {
      if (
        currentKey &&
        editableFields.some((field) => field.key === currentKey)
      ) {
        return currentKey;
      }

      return editableFields[0]?.key ?? null;
    });
  }, [item.codeActiveField, item.shinhanPdfActiveField, editableFields]);

  useEffect(() => {
    setFieldValue(selectedField?.value ?? '');
  }, [selectedField?.key, selectedField?.value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setLocalCode(next);
    setIsDirty(next !== item.code);
  };

  const handleApply = () => {
    setState({
      update: (state) =>
        changeItems(state, [item.id], (i) =>
          i.type === 'code'
            ? { ...i, code: localCode, compiledError: undefined }
            : i,
        ),
      commitToUndoStack: true,
    });
    setIsDirty(false);
  };

  const handleReset = () => {
    setLocalCode(item.code);
    setIsDirty(false);
  };

  const handleFieldSelect = (fieldKey: string) => {
    setSelectedFieldKey(fieldKey);
    focusFieldEditor();
    setState({
      update: (state) =>
        changeItems(state, [item.id], (i) =>
          i.type === 'code'
            ? {
                ...i,
                codeActiveField: fieldKey,
                codeActiveFields: [fieldKey],
              }
            : i,
        ),
      commitToUndoStack: false,
    });
  };

  const handleApplyField = () => {
    if (!selectedFieldKey) {
      return;
    }

    const nextCode = updateCodeEditableField(
      localCode,
      selectedFieldKey,
      fieldValue,
    );

    if (!nextCode) {
      return;
    }

    setLocalCode(nextCode);
    setIsDirty(false);
    setState({
      update: (state) =>
        changeItems(state, [item.id], (i) =>
          i.type === 'code'
            ? {
                ...i,
                code: nextCode,
                compiledError: undefined,
                codeActiveField: selectedFieldKey,
                codeActiveFields: [selectedFieldKey],
              }
            : i,
        ),
      commitToUndoStack: true,
    });
  };

  const isFieldDirty = Boolean(
    selectedField && fieldValue !== selectedField.value,
  );

  return (
    <div className="flex flex-col gap-sm p-sm">
      <div className="flex items-center justify-between">
        <span className="text-body-small text-sys-cont-neutral-default">
          {item.title || 'Generated Code'}
        </span>
        {isDirty && (
          <span className="text-comp-accent-xsmall text-sys-cont-primary-default">
            ● 수정됨
          </span>
        )}
      </div>

      {editableFields.length > 0 ? (
        <div className="flex flex-col gap-xs rounded-sm border border-sys-style-neutral-light bg-sys-bg-surface p-xs">
          <div className="text-comp-accent-small text-sys-cont-neutral-default">
            코드 데이터
          </div>
          <select
            value={selectedFieldKey ?? ''}
            onChange={(event) => handleFieldSelect(event.target.value)}
            className="w-full rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface px-xs py-2xs text-body-small text-sys-cont-neutral-default focus:border-sys-cont-primary-default focus:outline-none"
          >
            {editableFields.map((field) => (
              <option
                key={field.key}
                value={field.key}
              >
                {field.label}
              </option>
            ))}
          </select>
          {selectedField?.options ? (
            <select
              value={fieldValue}
              onChange={(event) => setFieldValue(event.target.value)}
              className="w-full rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface px-xs py-2xs text-body-small text-sys-cont-neutral-default focus:border-sys-cont-primary-default focus:outline-none"
            >
              {selectedField.options.map((option) => (
                <option
                  key={option}
                  value={option}
                >
                  {option === '' ? '(없음)' : option}
                </option>
              ))}
              {!selectedField.options.includes(fieldValue) ? (
                // vocab 밖 기존 값(카탈로그 드리프트·구버전 산출)을 첫 옵션으로 오인 표시하지 않도록
                // 현재 값을 명시 노출한다. 저장은 엔진 vocab 검증이 거부하므로 다른 값 선택만 가능.
                <option
                  key="__current-out-of-vocab"
                  value={fieldValue}
                >
                  {`(알 수 없는 값: ${fieldValue || '(빈 값)'})`}
                </option>
              ) : null}
            </select>
          ) : (
            <textarea
              ref={fieldTextAreaRef}
              value={fieldValue}
              onChange={(event) => setFieldValue(event.target.value)}
              spellCheck={false}
              rows={4}
              disabled={Boolean(selectedField?.readOnly)}
              className="w-full resize-y rounded-xs border border-sys-style-neutral-default bg-sys-bg-surface px-xs py-2xs text-body-small text-sys-cont-neutral-default focus:border-sys-cont-primary-default focus:outline-none disabled:text-sys-cont-neutral-disabled-default"
            />
          )}
          {selectedField?.readOnly ? (
            <span className="text-comp-accent-xsmall text-sys-cont-neutral-light">
              읽기 전용 — 씬 길이는 나레이션 음성 길이를 따릅니다
            </span>
          ) : null}
          <div className="flex justify-end">
            <Button
              type="button"
              size="small"
              hierarchy="primary-variant"
              onClick={handleApplyField}
              state={
                isFieldDirty && !selectedField?.readOnly
                  ? 'default'
                  : 'disabled'
              }
              disabled={!isFieldDirty || Boolean(selectedField?.readOnly)}
            >
              {selectedField?.options ? '선택 반영' : '텍스트 반영'}
            </Button>
          </div>
        </div>
      ) : null}

      <textarea
        value={localCode}
        onChange={handleChange}
        spellCheck={false}
        rows={20}
        className="w-full resize-y rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface px-sm py-xs font-mono text-comp-accent-xsmall text-sys-cont-neutral-default focus:border-sys-cont-primary-default focus:outline-none"
      />

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          size="small"
          hierarchy="tertiary"
          onClick={handleReset}
          state={isDirty ? 'default' : 'disabled'}
          disabled={!isDirty}
        >
          되돌리기
        </Button>
        <Button
          type="button"
          size="small"
          hierarchy="primary-variant"
          onClick={handleApply}
          state={isDirty ? 'default' : 'disabled'}
          disabled={!isDirty}
        >
          반영하기
        </Button>
      </div>

      {item.compiledError && (
        <div className="rounded-sm border border-sys-style-negative-default bg-sys-bg-surface p-xs text-comp-accent-xsmall text-sys-cont-negative-default">
          <div className="font-semibold">컴파일 오류</div>
          <pre className="mt-1 break-words whitespace-pre-wrap">
            {item.compiledError}
          </pre>
        </div>
      )}
    </div>
  );
}

export default CodeInspector;
